import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeContext, type PortalTheme } from './ThemeContext';

/** EAA-1844: sibling of ConsoleLayout's `NAV_COLLAPSE_STORAGE_KEY` — same
 *  "read once on mount, write on every change" localStorage convention. */
const THEME_STORAGE_KEY = 'portal.console.theme';

function readStoredTheme(): PortalTheme | null {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        return stored === 'dark' || stored === 'light' ? stored : null;
    } catch {
        // Private-mode / storage-disabled — fall through to the OS preference.
        return null;
    }
}

// EAA-1844: default-resolution order for a caller who has never touched the
// toggle. Stored preference wins first (an explicit past choice must survive
// an OS-level theme change); only when nothing is stored yet do we fall back
// to `prefers-color-scheme`, so a first-time visitor lands on whatever their
// OS already asked for instead of always starting light.
function resolveInitialTheme(): PortalTheme {
    const stored = readStoredTheme();
    if (stored) return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// EAA-1844: tokens.css's dark override block is selected by
// `:root[data-theme="dark"]` — light has no attribute at all (matching the
// bare `:root` block), rather than an explicit `data-theme="light"`.
function applyThemeAttribute(theme: PortalTheme): void {
    if (theme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
    } else {
        document.documentElement.removeAttribute('data-theme');
    }
}

/**
 * EAA-1844: theme context for the whole portal. Holds `'light' | 'dark'`,
 * mirrors the resolved value onto `document.documentElement`'s `data-theme`
 * attribute (see `applyThemeAttribute`), and persists every change to
 * localStorage so a returning admin keeps their chosen theme — the same
 * pattern `ConsoleLayout`'s nav-collapse preference already follows.
 *
 * Mounted in `main.tsx` above `UiKitProvider` so the shared kit's colour seam
 * can read the resolved theme too.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<PortalTheme>(resolveInitialTheme);

    useEffect(() => {
        applyThemeAttribute(theme);
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            // Best-effort persistence only — private mode / quota errors are not fatal.
        }
    }, [theme]);

    const setTheme = useCallback((next: PortalTheme) => setThemeState(next), []);
    const toggleTheme = useCallback(
        () => setThemeState((current) => (current === 'dark' ? 'light' : 'dark')),
        []
    );

    const value = useMemo(
        () => ({ theme, setTheme, toggleTheme }),
        [theme, setTheme, toggleTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
